import React, { useContext } from 'react';
import { useIntl } from 'react-intl';
import translations from './practicetypes.uha.i18n';
import { TableCell, TableRow } from '@mui/material';
import { StudiestedApiContext } from '../api/StudiestedApiContext';
import { useLoader } from '../api/useLoader';
import { NavLink, Route, Routes } from 'react-router-dom';
import Button from '../components/button';
import StandardTable from '../components/table';
import PanelHeader from '../components/panelheader/panelHeader';
import { PraksistypeMedAktivitetskravDto } from '../api/generated/studiested-generated-api';
import Spinner from '../components/spinner/spinner';
import { fullDateWithLocale } from '../date.utils';
import { Language, LanguageContext } from '../i18n/LanguageStore';
import { PageNotFoundWithoutLayout } from '../pages/pagenotfound';

const PracticeTypes = () => {
  const { formatMessage } = useIntl();
  const { selectedLanguage } = useContext(LanguageContext);
  const { praksistypeApi } = useContext(StudiestedApiContext);
  const { data, loading } = useLoader(
    () => praksistypeApi.praksistyperGet(),
    [],
  );

  if (loading) {
    return <Spinner />;
  }

  const praksistyper = data || [];
  const praksistyperWithoutAktivitetskrav = praksistyper.filter(
    (type) => type.aktivitetskrav.length === 0,
  );

  const praksistyperWithAktivitetskrav = praksistyper.filter(
    (type) => type.aktivitetskrav.length > 0,
  );

  return (
    <Routes>
      <Route
        path="withrequirements"
        element={
          <PraksistyperWithAktivitetskrav
            praksistyper={praksistyperWithAktivitetskrav}
            formatMessage={formatMessage}
            selectedLanguage={selectedLanguage}
          />
        }
      />
      <Route
        path="withoutrequirements"
        element={
          <PraksistyperWithoutAktivitetskrav
            praksistyper={praksistyperWithoutAktivitetskrav}
            formatMessage={formatMessage}
          />
        }
      />
      <Route path="/*" element={<PageNotFoundWithoutLayout />} />
    </Routes>
  );
};

export const PraksistyperWithoutAktivitetskrav: React.FC<
  React.PropsWithChildren<{
    praksistyper: PraksistypeMedAktivitetskravDto[];
    formatMessage: Function;
  }>
> = ({ praksistyper, formatMessage }) => {
  const labels = {
    nameHeader: formatMessage({ id: translations.nameHeader.id }),
    action: formatMessage({ id: translations.action.id }),
    createRequirement: formatMessage({ id: translations.createRequirement.id }),
    withoutRequirements: formatMessage({
      id: translations.withoutRequirements.id,
    }),
  };
  const tableHeaders = [{ title: labels.nameHeader }, { title: labels.action }];

  const tableRows = praksistyper.map((praksistype) => (
    <TableRow
      key={praksistype.praksistype.kode}
      id={praksistype.praksistype.kode}
    >
      <TableCell>{praksistype.praksistype.navn}</TableCell>
      <TableCell>
        {praksistype.kanEndre && (
          <NavLink
            to={`/myinstitution/practicetype/${praksistype.praksistype.kode}`}
          >
            <Button.Secondary>{labels.createRequirement}</Button.Secondary>
          </NavLink>
        )}
      </TableCell>
    </TableRow>
  ));
  return (
    <>
      <PanelHeader title={labels.withoutRequirements} />
      <StandardTable headers={tableHeaders} rows={tableRows} />
    </>
  );
};

const PraksistyperWithAktivitetskrav: React.FC<
  React.PropsWithChildren<{
    praksistyper: PraksistypeMedAktivitetskravDto[];
    formatMessage: Function;
    selectedLanguage: Language;
  }>
> = ({ praksistyper, formatMessage, selectedLanguage }) => {
  return (
    <>
      <PanelHeader
        title={formatMessage({ id: translations.withRequirements.id })}
      />
      <>
        {praksistyper.map((praksistype) => (
          <PraksisTypeWithAktivitetskrav
            key={praksistype.praksistype.kode}
            praksistype={praksistype}
            formatMessage={formatMessage}
            selectedLanguage={selectedLanguage}
          />
        ))}
      </>
    </>
  );
};

const PraksisTypeWithAktivitetskrav: React.FC<
  React.PropsWithChildren<{
    praksistype: PraksistypeMedAktivitetskravDto;
    formatMessage: Function;
    selectedLanguage: Language;
  }>
> = ({ praksistype, formatMessage, selectedLanguage }) => {
  const labels = {
    editRequirements: formatMessage({ id: translations.editRequirements.id }),
    seeRequirements: formatMessage({ id: translations.seeRequirements.id }),
    places: formatMessage({ id: translations.places.id }),
    lastChange: formatMessage({ id: translations.lastChange.id }),
    practiceIn: formatMessage(
      { id: translations.practiceIn.id },
      { place: praksistype.praksistype.navn },
    ),
  };

  const button = (
    <NavLink to={`/myinstitution/practicetype/${praksistype.praksistype.kode}`}>
      <Button.Secondary>
        {praksistype.kanEndre
          ? labels.editRequirements
          : labels.seeRequirements}
      </Button.Secondary>
    </NavLink>
  );

  const tableHeaders = [
    { title: labels.practiceIn },
    { title: labels.places },
    { title: labels.lastChange },
    { content: button },
  ];

  const tableRows = praksistype.aktivitetskrav.map((krav) => (
    <TableRow key={krav.id} id={krav.id}>
      <TableCell>{krav.navn}</TableCell>
      <TableCell>{krav.antallSteder}</TableCell>
      <TableCell>
        {krav.sistEndret &&
          fullDateWithLocale(new Date(krav.sistEndret!!), selectedLanguage)}
      </TableCell>
      <TableCell />
    </TableRow>
  ));

  return <StandardTable headers={tableHeaders} rows={tableRows} />;
};

export default PracticeTypes;
